import { Breadcrumb } from '~/lib';
import {
    SalesforceProduct,
    SalesforceProductPrice,
    SalesforceProductPromotionType,
    SalesforceProductSpecialPrice,
} from '~/lib/salesforce/types';

export type ProductSchemaPriceSource = Pick<SalesforceProduct, 'c_prices' | 'c_specialPrices'>;

export interface ProductSchemaPrices {
    offerPrice?: SalesforceProductPrice['value'];
    regularPrice?: SalesforceProductPrice['value'];
    promotype?: SalesforceProductPromotionType;
    shouldIncludeRegularPrice: boolean;
}

export type SchemaPriceType =
    | 'https://schema.org/ListPrice'
    | 'https://schema.org/StrikethroughPrice';

const OFFER_NAMES: Partial<Record<SalesforceProductPromotionType, string>> = {
    [SalesforceProductPromotionType.CHRISTMAS_PRICE]: 'Julepris',
    [SalesforceProductPromotionType.DISCOUNT_PRICE]: 'Tilbud',
};

/** The price Salesforce flags as the one to publish. */
export const getProductSchemaPrice = (prices?: SalesforceProductPrice[]) =>
    prices?.find((price) => price.schema);

/** The price Salesforce flags as explicitly not for publication, shown struck through. */
export const getProductNonSchemaPrice = (prices?: SalesforceProductPrice[]) =>
    prices?.find((price) => price.schema === false);

export const shouldIncludeRegularPriceInSchema = (prices?: SalesforceProductPrice[]) => {
    if (!prices) return false;

    return prices.some(
        (price) =>
            price.promotype === SalesforceProductPromotionType.DISCOUNT_PRICE ||
            price.promotype === SalesforceProductPromotionType.SPECIAL_PRICE ||
            price.promotype === SalesforceProductPromotionType.CHRISTMAS_PRICE,
    );
};

export const getSchemaPriceType = (promotype: SalesforceProductPromotionType): SchemaPriceType =>
    promotype === SalesforceProductPromotionType.SPECIAL_PRICE
        ? 'https://schema.org/ListPrice'
        : 'https://schema.org/StrikethroughPrice';

export const getProductSpecialPriceSchemaPrices = (
    specialPrices?: Pick<
        SalesforceProductSpecialPrice,
        'promotype' | 'effectivePrice' | 'retailPrice'
    >,
): ProductSchemaPrices | undefined => {
    if (specialPrices?.promotype !== SalesforceProductPromotionType.SPECIAL_PRICE) return undefined;

    if (specialPrices.effectivePrice == null || specialPrices.retailPrice == null) return undefined;

    return {
        offerPrice: specialPrices.effectivePrice,
        regularPrice: specialPrices.retailPrice,
        promotype: SalesforceProductPromotionType.SPECIAL_PRICE,
        shouldIncludeRegularPrice: true,
    };
};

export const getProductPriceListSchemaPrices = (
    prices?: SalesforceProductPrice[],
): ProductSchemaPrices => {
    const shouldIncludeRegularPrice = shouldIncludeRegularPriceInSchema(prices);
    const schemaPrice = getProductSchemaPrice(prices);

    return {
        offerPrice: schemaPrice?.value,
        regularPrice: shouldIncludeRegularPrice
            ? getProductNonSchemaPrice(prices)?.value
            : undefined,
        promotype: schemaPrice?.promotype,
        shouldIncludeRegularPrice,
    };
};

export const getProductSchemaPrices = (
    product?: ProductSchemaPriceSource | null,
): ProductSchemaPrices => {
    return (
        getProductSpecialPriceSchemaPrices(product?.c_specialPrices) ??
        getProductPriceListSchemaPrices(product?.c_prices)
    );
};

/* -------------------------------------------------------------------------- */
/* Schema graph (IMC-2835)                                                    */
/* -------------------------------------------------------------------------- */

export type SchemaReference = { '@id': string };

/**
 * `@id`s owned by the front page graph HomePageSchema.
 *
 * Referencing them keeps Imerco described once per site instead of once per
 * page. They must stay in sync with `HomePageSchema`; when both tickets are on
 * the same branch these should move into a single shared module.
 */
export const getSiteEntityIds = (siteUrl: string) => ({
    organizationId: `${siteUrl}/#organization`,
    memberProgramId: `${siteUrl}/#imerco-plus`,
    memberProgramTierId: `${siteUrl}/#imerco-plus-tier`,
});

export const getProductEntityIds = (productUrl: string, masterId?: string, siteUrl = '') => ({
    productId: `${productUrl}#product`,
    offerId: `${productUrl}#offer`,
    breadcrumbId: `${productUrl}#breadcrumb`,
    /**
     * Derived from the master id rather than the current URL, so every variant
     * page points at the same ProductGroup entity.
     */
    productGroupId: masterId ? `${siteUrl}/#product-group-${masterId}` : undefined,
});

/** Absolute product URL, built the same way as the canonical in `PDPMetaFields`. */
export const getAbsoluteUrl = (path: string | undefined, siteUrl: string) => {
    if (!path) return undefined;

    try {
        return new URL(path, `${siteUrl}/`).toString();
    } catch {
        return undefined;
    }
};

/* -------------------------------------------------------------------------- */
/* Dimensions                                                                 */
/* -------------------------------------------------------------------------- */

export type QuantitativeValueSchema = {
    '@type': 'QuantitativeValue';
    value: number;
    unitText: string;
    unitCode?: string;
};

/** UN/CEFACT codes for the units Inriver delivers. */
const UNIT_CODES: Record<string, string> = {
    mm: 'MMT',
    cm: 'CMT',
    dm: 'DMT',
    m: 'MTR',
    g: 'GRM',
    kg: 'KGM',
};

const toQuantitativeValue = (
    value: unknown,
    unit?: string,
): QuantitativeValueSchema | undefined => {
    if (value === null || value === undefined || value === '' || !unit) return undefined;

    const numeric = Number(String(value).replace(',', '.'));

    if (!Number.isFinite(numeric) || numeric <= 0) return undefined;

    const unitText = unit.trim();
    const unitCode = UNIT_CODES[unitText.toLowerCase()];

    return {
        '@type': 'QuantitativeValue',
        value: numeric,
        unitText,
        ...(unitCode ? { unitCode } : {}),
    };
};

export type ProductDimensionsSchema = {
    height?: QuantitativeValueSchema;
    width?: QuantitativeValueSchema;
    depth?: QuantitativeValueSchema;
    weight?: QuantitativeValueSchema;
};

/**
 * schema.org has no `length`, so `c_dimLength` is used as the depth fallback -
 * the two are alternatives in the product data, never both.
 */
export const getProductDimensions = (product: SalesforceProduct): ProductDimensionsSchema => {
    const unit = product.c_dimMessurementUnit;

    const height = toQuantitativeValue(product.c_dimHeight, unit);
    const width = toQuantitativeValue(product.c_dimWidth, unit);
    const depth =
        toQuantitativeValue(product.c_dimDepth, unit) ??
        toQuantitativeValue(product.c_dimLength, unit);
    const weight = toQuantitativeValue(product.c_dimWeight, product.c_dimWeightUnit);

    return {
        ...(height ? { height } : {}),
        ...(width ? { width } : {}),
        ...(depth ? { depth } : {}),
        ...(weight ? { weight } : {}),
    };
};

/* -------------------------------------------------------------------------- */
/* Offer pricing                                                              */
/* -------------------------------------------------------------------------- */

export type UnitPriceSpecificationSchema = {
    '@type': 'UnitPriceSpecification';
    price: SalesforceProductPrice['value'];
    priceCurrency?: string;
    priceType?: string;
    validForMemberTier?: SchemaReference;
};

export type OfferPricing = {
    name?: string;
    price?: SalesforceProductPrice['value'];
    priceSpecification: UnitPriceSpecificationSchema[];
};

export const getMemberPrice = (prices?: SalesforceProductPrice[]) =>
    prices?.find((price) => price.promotype === SalesforceProductPromotionType.MEMBER_PRICE);

export const getNonMemberPrice = (prices?: SalesforceProductPrice[]) =>
    prices?.find((price) => price.promotype !== SalesforceProductPromotionType.MEMBER_PRICE);

export const getOfferPricing = (
    product: SalesforceProduct,
    currency: string | undefined,
    memberProgramTierId: string,
    countdownRegularPrice?: number | null,
): OfferPricing => {
    // Salesforce already prices the product with a promotion that is still counting down, but it
    // cannot be bought at that price until the countdown ends, so neither sale nor strikethrough.
    if (countdownRegularPrice != null) {
        return { price: countdownRegularPrice, priceSpecification: [] };
    }

    const memberPrice = getMemberPrice(product.c_prices);

    if (memberPrice) {
        const nonMemberPrice = getNonMemberPrice(product.c_prices);

        return {
            price: nonMemberPrice?.value ?? product.price,
            priceSpecification: [
                {
                    '@type': 'UnitPriceSpecification',
                    price: memberPrice.value,
                    ...(currency ? { priceCurrency: currency } : {}),
                    validForMemberTier: { '@id': memberProgramTierId },
                },
            ],
        };
    }

    const { regularPrice, offerPrice, promotype, shouldIncludeRegularPrice } =
        getProductSchemaPrices(product);
    const shouldAddRegularPriceSpecification =
        shouldIncludeRegularPrice && regularPrice != null && promotype != null;

    return {
        name: promotype ? OFFER_NAMES[promotype] : undefined,
        price: offerPrice,
        priceSpecification: shouldAddRegularPriceSpecification
            ? [
                  {
                      '@type': 'UnitPriceSpecification',
                      price: regularPrice,
                      ...(currency ? { priceCurrency: currency } : {}),
                      priceType: getSchemaPriceType(promotype),
                  },
              ]
            : [],
    };
};

export const getAvailability = (product: SalesforceProduct) => {
    if (product.c_in_stock) return 'https://schema.org/InStock';
    if (product.c_in_stock_store) return 'https://schema.org/InStoreOnly';

    return 'https://schema.org/OutOfStock';
};

/* -------------------------------------------------------------------------- */
/* Reviews                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * A review as this module needs it. Deliberately not the Yotpo shape: the graph
 * builder takes reviews as an input so it does not care where they came from, and
 * a different source later means a new adapter, not a change here.
 */
export type ReviewSource = {
    rating: number;
    title?: string;
    content: string;
    author: string;
    createdAt?: string;
};

export type ReviewSchema = {
    '@type': 'Review';
    reviewRating: {
        '@type': 'Rating';
        ratingValue: number;
        bestRating: number;
    };
    author: { '@type': 'Person'; name: string };
    reviewBody: string;
    name?: string;
    datePublished?: string;
};

const BEST_RATING = 5;
const MAX_REVIEWS = 2;

/**
 * Top-rated reviews only, newest first so the markup does not go stale while the
 * widget shows more recent ones.
 */
export const getProductReviews = (reviews: ReviewSource[] = []): ReviewSchema[] =>
    reviews
        .filter((review) => review.rating >= BEST_RATING && !!review.content && !!review.author)
        .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
        .slice(0, MAX_REVIEWS)
        .map((review) => ({
            '@type': 'Review' as const,
            reviewRating: {
                '@type': 'Rating' as const,
                ratingValue: review.rating,
                bestRating: BEST_RATING,
            },
            author: { '@type': 'Person' as const, name: review.author },
            reviewBody: review.content,
            ...(review.title ? { name: review.title } : {}),
            ...(review.createdAt ? { datePublished: review.createdAt } : {}),
        }));

/* -------------------------------------------------------------------------- */
/* Graph                                                                      */
/* -------------------------------------------------------------------------- */

type BreadcrumbListItem = {
    '@type': 'ListItem';
    position: number;
    name?: string;
    item: string;
};

type SchemaNode = Record<string, unknown>;

export type PdpSchemaGraph = {
    '@context': 'https://schema.org';
    '@graph': SchemaNode[];
};

export type BuildPdpSchemaGraphArgs = {
    product: SalesforceProduct;
    breadcrumb?: Breadcrumb;
    siteUrl: string;
    /** Already resolved through the image loader by the caller. */
    imageUrls?: string[];
    /** Top reviews, from whichever source supplied them. */
    reviews?: ReviewSource[];
    /**
     * The regular price while a promotion countdown holds the discount back. The offer then states
     * only this price, as the discounted one cannot be bought yet.
     */
    countdownRegularPrice?: number | null;
};

const GTIN13_PATTERN = /^\d{13}$/;

/**
 * `hasVariant` points at the sibling variant PDPs by `@id`, using the same
 * `{url}#product` convention the product list pages use, so the graph links up
 * across pages without restating each variant here.
 */
const buildProductGroup = (
    product: SalesforceProduct,
    productGroupId: string,
    siteUrl: string,
): SchemaNode => {
    const variesBy = [
        ...(product.variationValues?.supplierColour ? ['https://schema.org/color'] : []),
        ...(product.variationValues?.size ? ['https://schema.org/size'] : []),
    ];

    const variantReferences = (product.c_variants ?? []).flatMap((variant) => {
        const variantUrl = getAbsoluteUrl(variant.c_pageURL, siteUrl);

        return variantUrl ? [{ '@id': `${variantUrl}#product` }] : [];
    });

    return {
        '@type': 'ProductGroup',
        '@id': productGroupId,
        productGroupID: product.master?.masterId,
        name: product.master?.name || product.name,
        description: [product.name, product.c_material].filter(Boolean).join(' - '),
        brand: {
            '@type': 'Brand',
            name: product.brand,
        },
        ...(product.c_ProductMainCategory ? { category: product.c_ProductMainCategory } : {}),
        ...(variesBy.length ? { variesBy } : {}),
        ...(variantReferences.length ? { hasVariant: variantReferences } : {}),
    };
};

export const buildPdpSchemaGraph = ({
    product,
    breadcrumb,
    siteUrl,
    imageUrls,
    reviews = [],
    countdownRegularPrice,
}: BuildPdpSchemaGraphArgs): PdpSchemaGraph => {
    const productUrl = getAbsoluteUrl(product.c_pageURL, siteUrl) ?? `${siteUrl}/`;
    const masterId = product.master?.masterId;

    const { organizationId, memberProgramId, memberProgramTierId } = getSiteEntityIds(siteUrl);
    const { productId, offerId, breadcrumbId, productGroupId } = getProductEntityIds(
        productUrl,
        masterId,
        siteUrl,
    );

    const currency = product.currency;
    const dimensions = getProductDimensions(product);
    const reviewSchemas = getProductReviews(reviews);
    const { name, price, priceSpecification } = getOfferPricing(
        product,
        currency,
        memberProgramTierId,
        countdownRegularPrice,
    );

    const variants = product.c_variants ?? [];
    const hasVariants = variants.length > 1;

    const breadcrumbItems: BreadcrumbListItem[] = (breadcrumb?.items ?? [])
        .filter((item) => !!item.url)
        .map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.title,
            item: siteUrl + item.url,
        }));

    const graph: SchemaNode[] = [];

    /* Offer ---------------------------------------------------------------- */
    /**
     * Nested on the SalesforceProduct rather than referenced as a sibling node: Google's
     * SalesforceProduct parser reads `offers` off the SalesforceProduct itself, so keeping it inline
     * avoids depending on `@id` resolution for a required field. It still carries
     * an `@id`, so other entities can point at it.
     */
    const offer: SchemaNode = {
        '@type': 'Offer',
        '@id': offerId,
        ...(name ? { name } : {}),
        price,
        priceCurrency: currency,
        availability: getAvailability(product),
        itemCondition: 'https://schema.org/NewCondition',
        url: productUrl,
        seller: { '@id': organizationId },
        ...(priceSpecification.length ? { priceSpecification } : {}),
        shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'DK',
            },
        },
    };

    /* ProductGroup --------------------------------------------------------- */
    const productGroup =
        hasVariants && productGroupId
            ? buildProductGroup(product, productGroupId, siteUrl)
            : undefined;

    /* Product -------------------------------------------------------------- */
    graph.push({
        '@type': 'Product',
        '@id': productId,
        name: product.name,
        sku: product.id,
        ...(product.ean && GTIN13_PATTERN.test(String(product.ean))
            ? { gtin13: String(product.ean) }
            : {}),
        ...(product.c_modelNumber ? { mpn: product.c_modelNumber } : {}),
        url: productUrl,
        ...(imageUrls?.length ? { image: imageUrls } : {}),
        description: product.longDescription || product.shortDescription || '',
        brand: {
            '@type': 'Brand',
            name: product.brand,
        },
        ...(product.variationValues?.supplierColour
            ? { color: product.variationValues.supplierColour }
            : {}),
        ...(product.variationValues?.size ? { size: product.variationValues.size } : {}),
        ...(product.c_material ? { material: product.c_material } : {}),
        ...(product.c_ProductMainCategory ? { category: product.c_ProductMainCategory } : {}),
        ...dimensions,
        ...(product.c_yotpoReviewCount
            ? {
                  aggregateRating: {
                      '@type': 'AggregateRating',
                      ratingValue: product.c_yotpoAverageRating,
                      ratingCount: product.c_yotpoReviewCount,
                  },
              }
            : {}),
        ...(reviewSchemas.length ? { review: reviewSchemas } : {}),
        offers: offer,
        ...(productGroup ? { inProductGroupWithID: masterId, isVariantOf: productGroup } : {}),
    });

    /* Front page entities referenced above --------------------------------- */
    /**
     * Minimal restatements of the front page entities so `seller` and
     * `validForMemberTier` resolve on this page too. Same `@id`, and a strict
     * subset of the front page properties - nothing conflicting.
     */
    graph.push({
        '@type': 'OnlineStore',
        '@id': organizationId,
        name: 'Imerco',
        url: `${siteUrl}/`,
    });

    if (getMemberPrice(product.c_prices)) {
        graph.push({
            '@type': 'MemberProgramTier',
            '@id': memberProgramTierId,
            name: 'ImercoPlus',
            isTierOf: { '@id': memberProgramId },
        });
    }

    /* BreadcrumbList ------------------------------------------------------ */
    if (breadcrumbItems.length) {
        graph.push({
            '@type': 'BreadcrumbList',
            '@id': breadcrumbId,
            itemListElement: breadcrumbItems,
        });
    }

    return {
        '@context': 'https://schema.org',
        '@graph': graph,
    };
};
